// Holder for globally required settings/constants
import { TARGET_APPS, DATABASES, DATABASES_BY_APP, KNOWN_APPS } from './appIdentifiers.js';

const TOAST_DEFAULT_CLOSE_TIMEOUT = 20000; // 20 seconds

const defaultTranslationLanguages = [
    {
        displayName: 'English (Default)',
        language: 'en'
    },
    {
        displayName: 'Afrikaans',
        language: 'af'
    },
    {
        displayName: 'Arabic',
        language: 'ar'
    },
    {
        displayName: 'Chinese (Simplified)',
        language: 'zh-CN'
    },
    {
        displayName: 'Chinese (Traditional)',
        language: 'zh-TW'
    },
    {
        displayName: 'Dutch',
        language: 'nl'
    },
    {
        displayName: 'Finnish',
        language: 'fi'
    },
    {
        displayName: 'French',
        language: 'fr'
    },
    {
        displayName: 'German',
        language: 'de'
    },
    {
        displayName: 'Greek',
        language: 'el'
    },
    {
        displayName: 'Hebrew',
        language: 'he'
    },
    {
        displayName: 'Hindi',
        language: 'hi'
    },
    {
        displayName: 'Italian',
        language: 'it'
    },
    {
        displayName: 'Japanese',
        language: 'ja'
    },
    {
        displayName: 'Korean',
        language: 'ko'
    },
    {
        displayName: 'Latvian',
        language: 'lv'
    },
    {
        displayName: 'Norwegian (Bokmål)',
        language: 'no'
    },
    {
        displayName: 'Polish',
        language: 'pl'
    },
    {
        displayName: 'Portuguese',
        language: 'pt'
    },
    {
        displayName: 'Russian',
        language: 'ru'
    },
    {
        displayName: 'Spanish',
        language: 'es'
    },
    {
        displayName: 'Swedish',
        language: 'sv'
    },
    {
        displayName: 'Tagalog (Filipino)',
        language: 'tl'
    },
    {
        displayName: 'Turkish',
        language: 'tr'
    },
    {
        displayName: 'Ukrainian',
        language: 'uk'
    },
    {
        displayName: 'Urdu',
        language: 'ur'
    },
    {
        displayName: 'Vietnamese',
        language: 'vi'
    },
];


const fieldPatterns = {
    EMAIL: '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$',
    PASSWORD: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$',
    PHONE_NUMBER: '^\\+[1-9]\\d{1,14}$',
    USER_CODE: '[0-9A-Z]+',
    UNIQUE_CODE: '[0-9A-Z]+',
    SIGN_UP_CODE: '[0-9A-Z]+',
    DISPLAY_NAME: '[\\d\\w\\W]{4,48}',
    MFA_CODE: '[0-9]{6}',
    NUMBER_RATIO: '^\\d+:\\d+$'
};

const fieldTypes = {
    TEXT_FIELD: 'textfield',
    TEXT_BOX: 'textbox',
    RICH_CONTENT_AREA: 'richcontentarea',
    NUMBER_FIELD: 'numberfield',
    NUMBER_RATIO_FIELD: 'numberratiofield',
    PHONE_NUMBER_FIELD: 'phonenumberfield',
    EMAIL_FIELD: 'emailfield',
    PASSWORD_FIELD: 'passwordfield',
    TIMESTAMP: 'timestamp',
    CHECK_BOX: 'checkbox',
    DROP_DOWN: 'dropdown',
    RADIO_GROUP: 'radiogroup',
    SELECT_GROUP: 'selectgroup',
    ENTITIES: 'entities',
    GLOBAL_VARIABLE: 'globalvariable',
    COLOR_PICKER: 'colorpicker',
};


// Button states
const buttonStates = {
    ERROR: 'button_state_error',
    WARN: 'button_state_warn',
    WAITING: 'button_state_waiting',
    NEUTRAL: 'button_state_neutral',
    SUCCESS: 'button_state_success',
    NONE: 'button_state_none'
};

// Field states
const fieldStates = {
    ERROR: 'field_state_error',
    WARN: 'field_state_warn',
    WAITING: 'field_state_waiting',
    NEUTRAL: 'field_state_neutral',
    SUCCESS: 'field_state_success'
};


// Floating menu row types
const floatingMenuRowTypes = {
    STANDARD: 'standard_floating_menu_row',
    SECTION_TITLE: 'section_title_floating_menu_row',
    CHECK_BOX: 'check_box_floating_menu_row',
    RADIO_SELECT: 'radio_select_floating_menu_row',
    COLOR: 'color_floating_menu_row',
    SIZE: 'size_floating_menu_row'
};

const richContentItemTypes = {
    TITLE: 'rca_item_title',
    TEXTBLOCK: 'rca_item_textblock',
    LINK: 'rca_item_link',
    MEDIA: 'rca_item_media',
    EMBED: 'rca_item_embed',
    RESOURCE: 'rca_item_resource',
    COLLECTION: 'rca_item_collection'
};

// translation types
const translationTypes = {
    PAGE: 'translation-type-page',
    STRING: 'translation-type-string'
};


// Notification types
const notificationTypes = {
    WARN: 'notification_warn',
    ERROR: 'notification_error',
    SUCCESS: 'notification_success',
    NEUTRAL: 'notification_neutral'
};

// Searchable Types
const searchableTypes = {
    RESOURCE: 'searchable_type_resource',
    COLLECTION: 'searchable_type_collection'
};

// Assignment submission types 
// eslint-disable-next-line no-unused-vars
const assignmentSubmissionTypes = {
    SUBMITTED: 'assignment_submission_submitted',
    UNSUBMITTED: 'assignment_submission_unsubmitted'
};

// App sections
const sections = {
    CLASSROOM: 'classroom_section',
    EXPLORE: 'explore_section',
    COLLECTIONS: 'collections_section',
    TEACH: 'teach_section',
    MANAGE: 'manage_section'
};

// Classroom sections
const classroomSections = {
    DASHBOARD: 'dashboard',
    ASSIGNMENTS: 'assignments',
    STUDENTS: 'students',
    LESSON_PLANNER: 'lesson-planner',
};

// collection sections
const collectionSectionsTypes = {
    USER: 'user_collection_section',
    SHARED: 'shared_collection_section'
};

// Document viewer modes
const documentViewerModes = {
    READ_ONLY: 'document_viewer_mode_read_only',
    WRITE: 'document_viewer_mode_write',
    ASSIGNMENT_SUBMITTER: 'document_viewer_mode_assignment_submitter',
    ASSIGNMENT_MARKER: 'document_viewer_mode_assignment_marker'
};

// Document viewer types
const documentViewerTypes = {
    RESOURCE: 'document_viewer_type_resource',
    COLLECTION: 'document_viewer_type_collection',
    ASSIGNMENT: 'document_viewer_type_assignment',
    TITLE: 'document_viewer_type_title'
};

// Annotation layer types
const annotationLayerTypes = {
    PRIVATE: 'private_annotation_layer',
    ASSIGNMENT: 'assignment_annotation_layer',
    CLASS: 'class_annotation_layer'
};

const roleRequire = {
    ANY: 'role_require_any',
    ALL: 'role_require_all'
};

// App roles
const roles = {
    // System
    SUPER_ADMIN: 'fabrick_admin', // can do anything

    // Publisher/Educator
    TENANT_ADMIN: 'fabrick_tenant_admin', // the administrator of the whole tenant, ie. Luke as ORIGO or Richard at BIOZONE
    CONTENT_EDITOR: 'fabrick_content_editor',
    DATA_ADMIN: 'fabrick_data_admin', // can upload and manage data 
    DATA_VIEWER: 'fabrick_data_viewer', // can view data related to the tenant

    // District/School/Teacher/Student
    DISTRICT_ADMIN: 'fabrick_district_admin', // Can administer all levels district, school, classes, teachers, students etc
    DISTRICT_VIEWER: 'fabrick_district_viewer', // Can view reports and information on all levels of district, school, teachers, students etc READ ONLY
    SCHOOL_ADMIN: 'fabrick_school_admin', // Can administer school, classes, teachers, students etc
    SCHOOL_VIEWER: 'fabrick_school_viewer', // Can view information about school, classes, teachers, students etc READ ONLY
    COURSE_ADMIN: 'fabrick_course_admin', // Can administer courses
    COURSE_VIEWER: 'fabrick_course_viewer', // Can view information about courses 
    CLASS_ADMIN: 'fabrick_class_admin', // Can administer students within course
    TEACHER_ADMIN: 'fabrick_teacher_admin',
    TEACHER: 'fabrick_teacher', // can teach a course
    STUDENT_LEADER: 'fabrick_student_leader', // can assign work to other students
    STUDENT: 'fabrick_student',

    // Free level
    CONTENT_VIEWER: 'fabrick_content_viewer',
};

const actions = {
    // --- Navigation/View --- //
    VIEW_CLASSROOM: 'view_classroom', // can see the classroom section
    VIEW_EXPLORE: 'view_explore', // can see the explore section
    VIEW_COLLECTIONS: 'view_collections', // can see the collections section
    VIEW_TEACH: 'view_teach', // can see the teach section
    VIEW_DASHBOARD: 'view_dashboard', // can see the classroom.dashboard
    VIEW_ASSIGNMENTS: 'view_assignments', // can see the classroom.assignments
    VIEW_STUDENTS: 'view_students', // can see the classroom.students 
    VIEW_GRADEBOOK: 'view_gradebook', // can see the classroom.gradebook
    VIEW_MANAGE: 'view_manage', // can see the manage section
    VIEW_ENTITIES: 'view_manage_entities', // can see the manage.entities
    VIEW_ENTITIES_TYPES: 'view_manage_entities_types', // can see the manage.entities.types
    VIEW_ENTITIES_EXPLORE: 'view_manage_entities_explore', // can see the manage.entities.explore
    VIEW_ENTITIES_ADVANCED: 'view_manage_entities_advanced', // can see the manage.entities.advanced

    // -- Manage -- //
    VIEW_PROVIDERS: 'view_providers', // can view providers
    VIEW_DATA_BROWSER: 'view_data_browser', // can view data browser
    BULK_UPLOAD: 'bulk_upload', // can bulk upload data
    IMPERSONATION: 'impersonation', // can impersonate other users
    VIEW_SCHOOL_YEAR: 'view_manage_school_year', // can view/run the school year rollover

    // -- Settings -- //
    VIEW_SETTINGS_MIGRATIONS: 'view_manage_settings_migrations', // can see/run migrations
    VIEW_SETTINGS_SEND_REMINDERS: 'view_manage_settings_send_reminders', // can send over allocation reminders
    VIEW_SETTINGS_APPLY_PROVIDERS: 'view_manage_settings_apply_providers', // can apply providers
    VIEW_SETTINGS_REMOVE_THUMBNAIL_LOCKS: 'view_manage_settings_remove_thumbnail_locks', // can remove thumbnail locks
    VIEW_SETTINGS_TYPES: 'view_manage_settings_types', // can manage entity types
    VIEW_SETTINGS_MEILISEARCH: 'view_manage_settings_meilisearch', // can view/manage meilisearch configuration
    VIEW_SETTINGS_RSS: 'view_manage_settings_rss', // can view/manage rss configuration
    VIEW_SETTINGS_STATUS_BANNER: 'view_manage_settings_status_banner', // can view/manage status banner

    // --- Courses --- //
    NEW_COURSE: 'new_course', // can create a new course
    DELETE_COURSE: 'delete_course', // can delete a course I created
    DELETE_COURSE_SCHOOL: 'delete_course_school', // can delete any course in my school 
    DELETE_COURSE_DISTRICT: 'delete_course_district', // can delete any course in my district
    DELETE_COURSE_TENANT: 'delete_course_tenant', // can delete any course in my tenant 
    DELETE_COURSE_ALL: 'delete_course_all', // can delete any course  

    // --- Students --- //
    VIEW_STUDENTS_CLASS: 'view_students_class', // view all students in a class I teach
    VIEW_STUDENTS_SCHOOL: 'view_students_school', // view all students in my school
    VIEW_STUDENTS_DISTRICT: 'view_students_district', // view all students in my district
    ADD_STUDENTS_CLASS: 'add_students_class', // add students to classes I teach
    ADD_STUDENTS_SCHOOL: 'add_students_school', // add students to my school
    ADD_STUDENTS_DISTRICT: 'add_students_district', // add students to my district
    ADMINISTER_STUDENTS_CLASS: 'administer_students_class', // manage, change name etc students to classes I teach
    ADMINISTER_STUDENTS_SCHOOL: 'administer_students_school', // ""  "" students to my school
    ADMINISTER_STUDENTS_DISTRICT: 'administer_students_district', // "" students to my district
    ADMINISTER_STUDENTS_ALL: 'administer_students_all', // "" "" all students of tenant
    DELETE_STUDENTS_CLASS: 'delete_students_class', // manage, change name etc students to classes I teach
    DELETE_STUDENTS_SCHOOL: 'delete_students_school', // ""  "" students to my school
    DELETE_STUDENTS_DISTRICT: 'delete_students_district', // "" students to my district
    DELETE_STUDENTS_ALL: 'delete_students_all', // "" "" all students
    EMAIL_STUDENT_GUARDIAN_CLASS: 'email_student_guardian', // can email the students guardian for students in my class

    // --- Roles --- //
    CREATE_CUSTOM_ROLE: 'create_custom_role', // can create new roles
    ASSIGN_ROLES_CLASS: 'assign_roles_class', // can assign roles within the class I teach
    ASSIGN_ROLES_SCHOOL: 'assign_roles_school', // can assign roles within my school
    ASSIGN_ROLES_DISTRICT: 'assign_roles_district', // can assign roles within my district
    ASSIGN_ROLES_TENANT: 'assign_roles_tenant', // can assign roles within my tenant
    ASSIGN_ROLES_ALL: 'assign_roles_all', // can assign roles to anyone

    // --- System --- //
    NEW_TENANT: 'new_tenant', // create new tenant
    DELETE_TENANT: 'delete_tenant', // delete tenant
    ADMINISTOR_TENANT: 'administor_tenant', // administor, update tenant

    // --- Assignments --- //
    NEW_ASSIGNMENT: 'new_assignment', // create assignment for all courses I teach
    GRADE_ASSIGNMENT_STUDENT: 'grade_assignment_student', // can grade an individuals assignment


    // --- core content --- //
    UPLOAD_CONTENT: 'upload_content', // can upload personal content

    // --- Content --- //
    TEXT_TO_SPEECH: 'text_to_speech' // can use text to speech
};

const contentActions = {

    // --- Content --- //
    VIEW_STUDENT_EDITION: 'view_student_edition_content',
    VIEW_TEACHER_EDITION: 'view_teacher_edition_content',
    VIEW_MODEL_ANSWERS: 'view_model_answers_content',
    VIEW_WEBLINKS_RESOURCES: 'view_weblinks_resources_content',
    VIEW_3D_MODELS_RESOURCES: 'view_3d_models_resources_content',
    VIEW_TEACHER_INTENDED_RESOURCES: 'view_teacher_intended_resources_content',
    VIEW_VIDEO_RESOURCES: 'view_video_resources_content',
    VIEW_SIMULATIONS_RESOURCES: 'view_simulations_resources_content',
    VIEW_PRESENTATION_MEDIA_RESOURCES: 'view_presentation_media_resources_content',
    VIEW_FILE_DOWNLOADS_RESOURCES: 'view_file_downloads_resources_content',

    // --- Annotations --- //
    CREATE_TEXT_ANNOTATIONS: 'create_text_annotations',
    CREATE_DRAWING_ANNOTATIONS: 'create_drawing_annotations',
    CREATE_COMMENT_ANNOTATIONS: 'create_comment_annotations',
    EDIT_QUESTION_FIELD_ANNOTATIONS: 'edit_question_field_annotations',

    // --- Accessibility --- //
    ACCESS_TEXT_TRANSLATION: 'access_text_translation',
    ACCESS_TEXT_TO_SPEECH: 'access_text_to_speech',

    // --- Announcements --- //
    VIEW_ANNOUNCEMENTS_CLASS: 'view_course_anouncements_class', // view course anouncements for courses I am enrolled in or teach
    VIEW_ANNOUNCEMENTS_TEACHER: 'view_course_announcements_teacher', // view announcements marked as 'for teachers only'
    DELETE_ANNOUNCEMENT_MY: 'delete_course_announcement_my', // delete any announcements I created
    DELETE_ANNOUNCEMENT_CLASS: 'delete_course_announcement_class', // delete any announcement I or students in my class created
    DELETE_ANNOUNCEMENT_ANY: 'delete_course_announcement_any', // delete any announcement
    NEW_ANNOUNCEMENT_CLASS: 'new_course_announcement_class', // create new course announcement for the whole class
    NEW_ANNOUNCEMENT_TEACHER: 'new_course_announcement_teacher', // create new announcement for teacher to view only
    NEW_ANNOUNCEMENT_STUDENT: 'new_course_announcement_student', // create new announcement for individual students to view only
    NEW_ANNOUNCEMENT_SCHOOL: 'new_course_announcement_school', // create new announcement for whole school
    NEW_ANNOUNCEMENT_DISTRICT: 'new_course_announcement_district', // create new announcement for whole district
    NEW_ANNOUNCEMENT_ANY: 'new_course_announcement_any', // create course announcement and send to anyone

    // --- Assignments --- //
    CREATE_ASSIGNMENT: 'create_assignment', // create assignments
    COMPLETE_ASSIGNMENT: 'complete_assignment', // complete assignments
    MARK_ASSIGNMENT: 'mark_assignment', // mark assignments 
};

const actionRequire = {
    ANY: 'permission_require_any',
    ALL: 'permission_require_all'
};

const authProviders = {
    EMAIL: 'auth-email',
    GOOGLE: 'auth-google',
    CLEVER: 'auth-clever',
    CLASSLINK: 'auth-classlink'
};

const googleScopes = [
    'https://www.googleapis.com/auth/userinfo.profile',
    'https://www.googleapis.com/auth/classroom.courses.readonly',
    'https://www.googleapis.com/auth/classroom.rosters.readonly',
    'https://www.googleapis.com/auth/classroom.coursework.me.readonly',
    'https://www.googleapis.com/auth/classroom.coursework.students',
    'https://www.googleapis.com/auth/classroom.announcements',
    'https://www.googleapis.com/auth/classroom.topics',
    'https://www.googleapis.com/auth/classroom.guardianlinks.students.readonly',
    'https://www.googleapis.com/auth/classroom.guardianlinks.me.readonly',
    'https://www.googleapis.com/auth/classroom.profile.photos',
    'https://www.googleapis.com/auth/cloud-platform',
    'https://www.googleapis.com/auth/cloud-translation',
    'https://www.googleapis.com/auth/cloud-speech'
];

const userGroupTypes = {
    COURSE_SYSTEM: 'user_group_type_system',
    COURSE_CUSTOM: 'user_group_type_custom'
};

const classUserGroupActions = {
    DELETE_CLASS_USER_GROUP: 'delete_class_user_group',
    REPORT_CLASS_USER_GROUP: 'report_class_user_group',
    MESSAGE_CLASS_USER_GROUP: 'message_class_user_group',
    ASSIGN_CLASS_USER_GROUP: 'assign_class_user_group'
};

const errors = {
    USER_NOT_READY: 'error-user-not-ready'
};

const collectionFolderColors = {
    YELLOW: 'rgb(255, 226, 172)',
    PINK: 'rgb(255, 151, 194)',
    GREEN: 'rgb(167, 255, 167)',
    PURPLE: 'rgb(234, 167, 255)',
    BLUE: 'rgb(119, 239, 255)',
    ORANGE: 'rgb(255, 183, 116)'
};

const entityModalModes = {
    NEW: 'entity_modal_mode_new',
    EDIT: 'entity_modal_mode_edit'
};

// Manage section paths, keyed by semantic name.
// `prefix` lets each app mount the manage surface under a different route root
// (admin = '', education = '/manage').
function buildManageSections(prefix = '') {
    const p = (path) => `${prefix}${path}`;
    return {
        DASHBOARD: p('/dashboard'),
        SETTINGS: p('/settings'),
        SETTINGS_MEILISEARCH: p('/settings/meilisearch'),
        SETTINGS_MEILISEARCH_TASKS: p('/settings/meilisearch/tasks'),
        SETTINGS_MEILISEARCH_BOOKS: p('/settings/meilisearch/books'),
        COLLECTIONS: 'manage_collections_section',
        RESOURCES: 'manage_resources_section',
        ENTITIES: p('/entities'),
        ENTITY_TYPES: p('/settings/types'),
        SETTINGS_RSS: p('/settings/rss'),
        SETTINGS_STATUS_BANNER: p('/settings/status-banner'),
        SETTINGS_TAP_CODES: p('/settings/tap-codes'),
        SETTINGS_TAP_USERS: p('/settings/tap-users'),
        ENTITY_STRUCTURE: 'manage_entity_structure_section',
        ENTITY_BULK: p('/entities/bulk'),
        ENTITY_ADVANCED_SEARCH: p('/entities/search'),
        TITLE_MANAGEMENT: p('/entities/titles'),
        LEGACY_UPLOADER: p('/entities/legacy-uploader'),
        STAGING: p('/entities/staging'),
        PEOPLE: 'manage_people_section',
        ROLES: 'manage_roles_section',
        PROVIDERS: p('/providers'),
        SETUP_WIZARD: p('/setup-wizard'),
        SCHOOL_YEAR: p('/school-year'),
        SCHOOL_YEAR_PREVIOUS: p('/school-year/previous'),
        IMPERSONATION: p('/impersonation'),
        DATA_BROWSER: p('/data-browser'),
        DATA_BROWSER_ADMINS: p('/data-browser/admins'),
        DATA_BROWSER_DISTRICTS: p('/data-browser/districts'),
        DATA_BROWSER_SCHOOLS: p('/data-browser/schools'),
        DATA_BROWSER_TEACHERS: p('/data-browser/teachers'),
        DATA_BROWSER_STUDENTS: p('/data-browser/students'),
        DATA_BROWSER_CLASSES: p('/data-browser/classes'),
        DATA_BROWSER_COURSES: p('/data-browser/courses'),
        DATA_BROWSER_SUBSCRIPTIONS: p('/data-browser/subscriptions'),
        TAP_DATA_BROWSER: p('/tap/data-browser'),
        PERSONAL_DATA_BROWSER: p('/personal/data-browser'),
        DATA_MANAGEMENT: p('/data-management'),
        DATA_BROWSE: p('/data-management/browse'),
        DATA_ARCHIVE: p('/data-management/archive'),
        DATA_IMPORT_RUNS: p('/data-management/import-runs'),
        DATA_PENDING_DELETION: p('/data-management/pending-deletion'),
        TAP_DATA_MANAGEMENT: p('/tap/data-management'),
        PERSONAL_DATA_MANAGEMENT: p('/personal/data-management'),
    };
}

const syncStatus = {
    READY: 'READY',
    COMPLETED: 'COMPLETED',
    SYNCING: 'SYNCING',
    ERROR: 'ERROR',
    PAUSED: 'PAUSED',
    DELETING: 'DELETING',
};

/**
 * School year rollover vocabulary, mirroring
 * `apps/functions/constants.js` → `schoolYearRollover`.
 *
 * The school-year browser branches on `year.status` to decide whether a year
 * can be managed at all, so these stopped being cosmetic strings the moment
 * one page started serving both the current year and the closed ones.
 */
const schoolYear = {
    undoWindowDays: 14,
    yearStatus: {
        ACTIVE: 'active',
        PAST: 'past',
    },
    runStatus: {
        PENDING: 'pending',
        DRAFT: 'draft',
        EXECUTING: 'executing',
        COMPLETED: 'completed',
        FAILED: 'failed',
        UNDOING: 'undoing',
        UNDONE: 'undone',
        DISMISSED: 'dismissed',
    },
    // Why the management controls are hidden, as reported by
    // SchoolYearService.describeManageability. 'past-year' is derived on the
    // client from year.status; the rest are school-scoped and server-sent.
    manageabilityReasons: {
        PAST_YEAR: 'past-year',
        SSO_SCHOOL: 'sso-school',
        NO_PROVIDER: 'no-provider',
    },
};

const providerType = {
    CLEVER: 'CLEVER',
    CLASSLINK: 'CLASSLINK',
    MANUAL: 'MANUAL',
    TAP_CODES: 'TAP_CODES',
};

const providerTypes = [
    {
        displayName: 'Manual',
        name: providerType.MANUAL
    },
    {
        displayName: 'TA Codes',
        name: providerType.TAP_CODES
    },
    {
        displayName: 'ClassLink',
        name: providerType.CLASSLINK
    },
    {
        displayName: 'Clever',
        name: providerType.CLEVER
    },
];

const signUpUserTypes = {
    TAP: 'tap',
    STUDENT: 'student',
};

const COLLECTION_TYPES = {
    ANNOTATIONS: 'annotations',
    ASSIGNMENTS: 'assignments',
    AUDIT: 'audit',
    BULK_CODES: 'bulk-codes',
    EMAIL_TEMPLATES: 'mail-templates',
    ENTITIES_ARCHIVE: 'entities-archive',
    ENTITY_TYPE_FIELDS: 'entity-type-fields',
    ENTITY_TYPES: 'entity-types',
    FILE_CACHE_MARKERS: 'file-cache-markers',
    MAIL: 'mail',
    PROVIDERS: 'providers',
    REMINDERS: 'reminders',
    SUBMISSIONS: 'submissions',
    SUBSCRIPTIONS: 'subscriptions',
    TENANTS: 'tenants',
    TRANSLATION_PAGE_DICTIONARY: 'translation-page-dictionary',
    TRANSLATION_STRING_DICTIONARY: 'translation-string-dictionary',
    MARKETING_MATERIALS: 'marketing-materials',
    USERS: 'users',
    TAGS: 'tags',
    ROLES: 'roles',
    ACTIONS: 'actions',
    CONTENT_ACTIONS: 'content-actions',
    DISTRICTS: 'districts',
    DISTRICT_GROUPS: 'district-groups',
    SCHOOLS: 'schools',
    SCHOOL_GROUPS: 'school-groups',
    CLASSES: 'classes',
    CLASS_GROUPS: 'class-groups',
    COURSES: 'courses',
    COLLECTIONS: 'collections',
    RESOURCES: 'resources',
    NOTIFICATIONS: 'notifications',
    LICENCE_TEMPLATES: 'licence-templates',
    WOO_ORDERS: 'woo-orders',
};

const COLLECTION_FIELDS = {
    SUBSCRIPTIONS: 'vWf8DwHWIUgnvrOOppce',
    USERS: 'aaNlySNoTYnZSyzKTzSR',
    ASSIGNMENTS: 'JdNwCDKAnoxBITkUbcBF',
    ROLES: 'vTt5kiYH7WuIMo1VinFq',
    DISTRICTS: 'cRBP5bdeIRsx65jzgjiS',
    SCHOOLS: 'gZt53Vw8kBoTq1erRqws',
    ACTIONS: 'pxszENyoeE5yEZdmwqSr',
    CONTENT_ACTIONS: 'rfonsFwRmeU4Xnhy7mS3',
    CLASSES: 'I8QDUj76nfHdXTQgviLi',
    CLASS_GROUPS: 'XIHrIK4mO5gf3EnWly4g',
    COURSES: 'wsjq1dnb0EsV1c2asbxR',
    COLLECTIONS: 'HSc4i12Zr0c33j5WIosT',
    RESOURCES: 'Nk9EmFnpuw6YihV72DGQ',
    // Personal-licence entity types, created by
    // apps/functions/scripts/setup-personal-db.js with these fixed IDs.
    LICENCE_TEMPLATES: 'personalLicenceTmpl0',
    WOO_ORDERS: 'personalWooOrders000',
};

// Reverse of COLLECTION_FIELDS, with the user-facing label/type the
// manage UI (entity graph etc.) renders. Keyed by entity type document ID.
const entityTypesByID = {
    'aaNlySNoTYnZSyzKTzSR': {type: 'user', label: 'User'},
    'HSc4i12Zr0c33j5WIosT': {type: 'collection', label: 'Collection'},
    'Nk9EmFnpuw6YihV72DGQ': {type: 'resource', label: 'Resource'},
    'I8QDUj76nfHdXTQgviLi': {type: 'class', label: 'Class'},
    'XIHrIK4mO5gf3EnWly4g': {type: 'classGroup', label: 'Class Group'},
    '0uxgCSuLPdpLCP25S66B': {type: 'submission', label: 'Submission'},
    'XDDiiOIiYkeGxZkocHkY': {type: 'annotation', label: 'Annotation'},
    'vTt5kiYH7WuIMo1VinFq': {type: 'role', label: 'Role'},
    'cRBP5bdeIRsx65jzgjiS': {type: 'district', label: 'District'},
    'gZt53Vw8kBoTq1erRqws': {type: 'school', label: 'School'},
    'wsjq1dnb0EsV1c2asbxR': {type: 'course', label: 'Course'},
    'pxszENyoeE5yEZdmwqSr': {type: 'action', label: 'Action'},
    'rfonsFwRmeU4Xnhy7mS3': {type: 'contentAction', label: 'Content Action'},
    'personalLicenceTmpl0': {type: 'licenceTemplate', label: 'Licence Template'},
    'personalWooOrders000': {type: 'wooOrder', label: 'Woo Order'},
};

export {
    defaultTranslationLanguages,
    fieldPatterns,
    fieldTypes,
    errors,
    sections,
    translationTypes,
    userGroupTypes,
    classUserGroupActions,
    richContentItemTypes,
    classroomSections,
    documentViewerModes,
    documentViewerTypes,
    floatingMenuRowTypes,
    roleRequire,
    roles,
    contentActions,
    actionRequire,
    authProviders,
    googleScopes,
    actions,
    buttonStates,
    fieldStates,
    notificationTypes,
    searchableTypes,
    assignmentSubmissionTypes,
    collectionSectionsTypes,
    collectionFolderColors,
    annotationLayerTypes,
    entityModalModes,
    syncStatus,
    providerType,
    providerTypes,
    schoolYear,
    TARGET_APPS,
    DATABASES,
    DATABASES_BY_APP,
    KNOWN_APPS,
    signUpUserTypes,

    COLLECTION_TYPES,
    COLLECTION_FIELDS,
    entityTypesByID,
    TOAST_DEFAULT_CLOSE_TIMEOUT,
    buildManageSections,
};